import React, { Component } from "react";
import { Tooltip, Icon, Dropdown, Menu } from "antd";
import { uniqBy } from "lodash";
import Tabular from "../Tabular";
import fullProcessSequence from "./fullProcessSequence";
import { copyJSONToClipboard } from "../../helpers";
import {
  Wrapper,
  ViewChangerPanel,
  ViewWrapper,
  RadioGroup,
  RadioButton,
  HeaderButton,
  ContextTotal,
} from "./style";

// This is one Chart component that can generate various kinds of cartesian and polar charts,
// based on props in chart config.
class Visualisation extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayList: {},
      chartLegendItems: [],
      singleLevelRangeGroupList: {}, // To streamline chart clicking finding action from data.
      chartData: {},
      chartDataTotal: null,
      chartSeries: "",
      tableColumn: [],
      tableData: [],
      currentView: "chart",
      currentConfig: "",
      header: "",
    };

    this.handleClickToChangeView = this.handleClickToChangeView.bind(this);
    this.handleClickChartToAction = this.handleClickChartToAction.bind(this);
  }

  componentDidMount() {
    const { prefConfig } = this.props;
    fullProcessSequence(this, prefConfig); // Process data when receiving initial props.
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.data.data !== prevState.tableData) {
      return { tableData: nextProps.data.data };
    }

    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    const { tableData } = this.state;
    const { prefConfig } = this.props;
    // An array must convert into a string to be able to compare correctly.
    // Otherwise if a deep clone to the array is performed, it is considered
    // as a whole new object, thus is different and results oddities.
    if (
      JSON.stringify(prevState.tableData) !== JSON.stringify(tableData) ||
      prevProps.prefConfig !== prefConfig
    ) {
      fullProcessSequence(this, prefConfig);
    }
  }

  generateChartOptions = (key, chartConfig) => {
    let icon = "bar-chart";
    const { series, visType, title } = chartConfig;
    if (series === "polar") icon = "pie-chart";
    if (visType === "area") icon = "area-chart";
    if (visType === "line") icon = "line-chart";
    return (
      <Tooltip title={title} key={key}>
        <RadioButton key={key} value={key}>
          <Icon type={icon} />
        </RadioButton>
      </Tooltip>
    );
  };

  handleClickToChangeView(event) {
    const { value } = event.target;
    const { onChangePrefConfig } = this.props;
    // Clear the current config. This is needed because reChart
    // won't resize the graph properly if the image is not cleared.
    this.setState(
      {
        currentView: "",
        currentConfig: "",
        chartData: {},
        chartSeries: "",
        header: "",
      },
      () => {
        // Table don't have re-process the chart data.
        if (value === "table") {
          this.setState({
            currentView: event.target.value,
            currentConfig: "table",
            chartData: {},
            chartSeries: "",
            header: "",
          });
        } else {
          // If is not table, it must be a type of chart.
          // Re-run the processing to generate another chart.
          fullProcessSequence(this, value);
        }

        onChangePrefConfig(value);
      }
    );
  }

  // Clicking currently only supports bar and pie charts.
  handleClickChartToAction(pick) {
    const { onChartClick } = this.props;
    const { singleLevelRangeGroupList } = this.state;

    // Grab the suitable payload for pie chart or bar chart.
    // A pie chart will have outerRadius and uses click attribute in groups object.
    // A bar chart will have no outerRadius and uses click attribute in range object.
    const selected = pick.outerRadius ? pick.payload.payload : pick;

    if (onChartClick && singleLevelRangeGroupList[selected.name]) {
      onChartClick({
        select: selected,
        action: singleLevelRangeGroupList[selected.name].click,
      });
    }
  }

  returnTableColumnWithFiltersAndSorters() {
    const { tableColumn } = this.state;
    const { data } = this.props;
    return tableColumn.map((column) => {
      const { filters, title } = column;
      const { data: rows } = data;
      if (filters) {
        const uniqueFilterValues = uniqBy(
          rows.map((d) => ({
            text: String(d[title]) || "",
            value: String(d[title]) || "",
          })),
          "text"
        );
        return {
          ...column,
          filters: uniqueFilterValues,
          onFilter: (value, record) => String(record[title]) === value,
        };
      }
      return column;
    });
  }

  render() {
    const {
      data,
      width,
      height,
      prefConfig,
      context,
      onUnitListClick,
      unitList = [],
    } = this.props;

    const {
      displayList,
      chartData,
      chartDataTotal,
      chartSeries,
      chartLegendItems,
      tableData,
      currentView,
      currentConfig,
      singleLevelRangeGroupList,
      header,
    } = this.state;
    const config = currentConfig && displayList[currentConfig];
    let view;

    const tableColumnDefinitionWithFiltersAndSorters = this.returnTableColumnWithFiltersAndSorters();

    // Determine whether is a chart or a table.
    if (currentView !== "table") {
      if (!config) return false; // Quit early if no config.
      const ChartSeriesTagName = chartSeries;
      view = (
        <ChartSeriesTagName
          width={width}
          height={height}
          data={chartData}
          legendItems={chartLegendItems}
          elementMap={singleLevelRangeGroupList}
          labelMap={data ? data.dataLabelMapping : {}}
          labelSetForTicks={config.rangeLabelSet}
          labelSetForData={config.groupsLabelSet}
          xRefLine={config.xRefLine}
          yRefLine={config.yRefLine}
          visType={config.visType}
          color={config.color}
          px={0}
          {
            // TODO: This is a quick hack, until backend is returning action prop.
            // in chart config.
            ...config.range.WeekDay && {
              chartClick: (pick) => this.handleClickChartToAction(pick),
            }
          }
          {...config.range.Hour && {
            interval: Object.keys(config.range.Hour).length > 24 ? 11 : 1,
          }}
        />
      );
    } else {
      view = (
        <Tabular
          columns={tableColumnDefinitionWithFiltersAndSorters}
          dataSource={tableData.map((row, i) => ({ key: i, ...row }))}
          pagination={false}
          scroll={{ y: true }} // if true, scroll is enabled, if integer value is set, this will give height to table
          px="1"
          ml="2.5%"
          width="95%"
          fontSize=".75vw" // This resizes the font-size according to screen width.
          size="small"
          onRow={(record, index) =>
            data.tableOptionsByDataRow &&
            data.tableOptionsByDataRow[index] && {
              style: data.tableOptionsByDataRow[index],
            }
          }
        />
      );
    }

    return (
      <Wrapper p={1}>
        <ViewChangerPanel
          className="DCRPVisualisationViewChangerPanel"
          bg="inherit"
        >
          <RadioGroup
            onChange={this.handleClickToChangeView}
            defaultValue={prefConfig}
            color="clientPrimary"
          >
            {displayList &&
              Object.entries(displayList).map((list) =>
                this.generateChartOptions(list[0], list[1])
              )}
            <Tooltip title={`${data.title} (Data in tabular view)`}>
              <RadioButton value="table">
                <Icon type="bars" />
              </RadioButton>
            </Tooltip>
          </RadioGroup>
        </ViewChangerPanel>
        <h4 color="greyscale.3">
          {header || data.title}
          {unitList.length > 1 && (
            <Dropdown
              trigger="click"
              overlay={
                <Menu>
                  {unitList.map((unit) => (
                    <Menu.Item key={unit} onClick={onUnitListClick}>
                      {decodeURIComponent(unit)}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <HeaderButton icon="down" size="small" ml={1} />
            </Dropdown>
          )}
          {currentView === "table" && (
            <HeaderButton
              ml={1}
              size="small"
              onClick={() => copyJSONToClipboard(tableData)}
            >
              Copy to clipboard
            </HeaderButton>
          )}
        </h4>
        <ViewWrapper ref={this.element} p={1}>
          {view}
          {chartDataTotal ? (
            <ContextTotal>
              <span>
                {context ? `${context} ` : ""}
                Total:
              </span>
              <span>{chartDataTotal}</span>
            </ContextTotal>
          ) : (
            ""
          )}
        </ViewWrapper>
      </Wrapper>
    );
  }
}

export default Visualisation;
